import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Check, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { VOTE_OPTIONS } from '../lib/products';
import KrispButton from '../components/KrispButton';

const voterId = () => {
  let id = localStorage.getItem('pk_voter');
  if (!id) { id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem('pk_voter', id); }
  return id;
};

export default function VoteSection({ compact = false }) {
  const [choice, setChoice] = useState(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [voted, setVoted] = useState(localStorage.getItem('pk_voted'));

  useEffect(() => {
    api.get('/vote').then((r) => setResults(r.data)).catch(() => {});
  }, []);

  const cast = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      const { data } = await api.post('/vote', { option: choice, email: email || null, voter_id: voterId() });
      setResults({ counts: data.counts, total: data.total });
      localStorage.setItem('pk_voted', data.option);
      setVoted(data.option);
      toast(data.already_voted ? 'YOU ALREADY VOTED — LOCKED IN.' : 'VOTE COUNTED. GO BEYOND.');
    } catch {
      toast('VOTE FUMBLED. TRY AGAIN.');
    } finally { setBusy(false); }
  };

  const pct = (id) => (results && results.total ? Math.round((results.counts[id] / results.total) * 100) : 0);

  return (
    <section id="vote" data-testid="vote-section" className={`relative bg-cocoa text-cream overflow-hidden ${compact ? 'py-14' : 'py-20 md:py-28'} scroll-mt-24`}>
      <div className="absolute inset-0 opacity-[0.07] bg-[repeating-linear-gradient(135deg,transparent_0_28px,#F7B500_28px_30px)]" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-14 grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start">
        <div>
          <p className="font-cond text-xs tracking-[0.45em] text-kyellow flex items-center gap-2"><Zap size={14} /> FLAVOUR #3 · YOU DECIDE</p>
          <h2 className="font-display text-6xl md:text-8xl leading-[0.85] mt-3">COMING SOON.<br /><span className="text-kyellow">STAY TUNED.</span></h2>
          <p className="text-cream/70 mt-5 max-w-md">Two flavours are locked. The third is on you. Pick the one you'd put in your gym bag — the winner goes to the roaster.</p>
          <div className="mt-6 flex items-center gap-3 font-cond text-sm tracking-[0.25em] text-cream/60">
            <span className="w-2.5 h-2.5 rounded-full bg-kred animate-pulse" /> LIVE · <span data-testid="vote-total" className="text-cream">{results?.total ?? 0}</span> VOTES CAST
          </div>
        </div>

        <div>
          <div className="grid sm:grid-cols-2 gap-3">
            {VOTE_OPTIONS.map((o) => {
              const on = (voted || choice) === o.id;
              return (
                <button key={o.id} data-testid={`vote-option-${o.id}`} data-cursor="plus" disabled={!!voted} onClick={() => setChoice(o.id)}
                  className={`relative text-left rounded-2xl border-2 p-5 transition-all duration-200 overflow-hidden ${on ? 'border-kyellow bg-kyellow text-cocoa shadow-[5px_5px_0_#F7B500]' : 'border-cream/25 bg-cream/5 hover:border-cream/60'} ${voted && !on ? 'opacity-70' : ''}`}>
                  <span className="absolute left-0 bottom-0 h-1.5 transition-[width] duration-700" style={{ width: `${pct(o.id)}%`, background: on ? '#2A1010' : o.color }} />
                  <span className="flex items-center justify-between">
                    <span className="font-display text-3xl leading-none">{o.name}</span>
                    {on && <span className="w-7 h-7 rounded-full bg-cocoa text-kyellow flex items-center justify-center"><Check size={15} /></span>}
                  </span>
                  <span className={`block text-xs mt-2 ${on ? 'text-cocoa/80' : 'text-cream/60'}`}>{o.desc}</span>
                  <span data-testid={`vote-pct-${o.id}`} className={`block font-cond text-xs tracking-[0.2em] mt-3 ${on ? 'text-cocoa' : 'text-cream/70'}`}>{pct(o.id)}% · {results?.counts?.[o.id] ?? 0} VOTES</span>
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            {voted ? (
              <motion.div key="done" data-testid="vote-locked" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="mt-5 rounded-2xl border-2 border-kyellow/60 bg-kyellow/10 px-5 py-4 flex items-center gap-3">
                <Lock size={18} className="text-kyellow" />
                <p className="font-cond tracking-wider text-sm">YOUR VOTE IS LOCKED IN: <span className="text-kyellow">{VOTE_OPTIONS.find((o) => o.id === voted)?.name}</span>. WE'LL TELL YOU WHO WINS.</p>
              </motion.div>
            ) : (
              <motion.div key="form" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 flex flex-col sm:flex-row gap-3">
                <input data-testid="vote-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional) — get the result first" className="k-input flex-1" />
                <KrispButton testid="vote-submit" disabled={!choice || busy} onClick={cast} className="!px-7">{busy ? 'COUNTING...' : 'CAST MY VOTE →'}</KrispButton>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
